import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { projectTemplateService } from '../services/projectTemplateService'
import { ProjectInstance } from '../types/projectTemplate'
import AppHeader from '../components/AppHeader'
import NotificationDropdown from '../components/NotificationDropdown'
import SettingsDropdown from '../components/SettingsDropdown'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  EyeIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  ChevronLeftIcon,
  ListBulletIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline'

type ProjectStatus = 'planning' | 'in-progress' | 'review' | 'completed' | 'cancelled'
type ViewMode = 'grid' | 'timeline' | 'kanban'

export default function ProjectsPage() {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const [projects, setProjects] = useState<ProjectInstance[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [selectedProject, setSelectedProject] = useState<ProjectInstance | null>(null)
  const [showProjectModal, setShowProjectModal] = useState(false)

  useEffect(() => {
    if (user) {
      loadProjects()
    }
  }, [user, selectedStatus])

  const loadProjects = async () => {
    try {
      setIsLoading(true)
      const status = selectedStatus === 'all' ? undefined : selectedStatus
      const result = await projectTemplateService.getProjectInstances(user!.id, status)
      setProjects(result)
    } catch (error) {
      console.error('Error loading projects:', error)
      showError('Error', 'Failed to load projects')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateProjectStatus = async (projectId: string, newStatus: ProjectStatus) => {
    try {
      const result = await projectTemplateService.updateProjectStatus(projectId, newStatus)
      if (result.success) {
        showSuccess('Success', 'Project status updated')
        await loadProjects()
      } else {
        showError('Error', result.error || 'Failed to update project status')
      }
    } catch (error) {
      console.error('Error updating project status:', error)
      showError('Error', 'Failed to update project status')
    }
  }

  const filteredProjects = projects.filter(project => 
    project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.template?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const projectStats = {
    total: projects.length,
    inProgress: projects.filter(p => p.status === 'in-progress').length,
    planning: projects.filter(p => p.status === 'planning').length,
    completed: projects.filter(p => p.status === 'completed').length
  }

  if (isLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Floating orbs for glassmorphism background effect */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>

        {/* Header */}
        <AppHeader>
          <NotificationDropdown />
          <SettingsDropdown />
          <ProfileDropdown />
        </AppHeader>

        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader>
        <NotificationDropdown />
        <SettingsDropdown />
        <ProfileDropdown />
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Active Projects</h1>
              <p className="text-gray-400">Manage your current projects and track progress</p>
            </div>
            <button
              onClick={() => window.location.href = '/project-templates'}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Project
            </button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <div className="glass-mockup rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Projects</p>
                <p className="text-2xl font-bold text-white">{projectStats.total}</p>
              </div>
              <DocumentDuplicateIcon className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          <div className="glass-mockup rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">In Progress</p>
                <p className="text-2xl font-bold text-white">{projectStats.inProgress}</p>
              </div>
              <PlayIcon className="w-8 h-8 text-green-400" />
            </div>
          </div>

          <div className="glass-mockup rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Planning</p>
                <p className="text-2xl font-bold text-white">{projectStats.planning}</p>
              </div>
              <ClockIcon className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          <div className="glass-mockup rounded-xl border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Completed</p>
                <p className="text-2xl font-bold text-white">{projectStats.completed}</p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-purple-400" />
            </div>
          </div>
        </motion.div>

        {/* Filters and Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-xl border border-white/10 p-6 mb-6"
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-3 py-3 glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent w-full"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-400" />
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as ProjectStatus | 'all')}
                className="glass-mockup border border-white/10 rounded-lg px-3 py-3 text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
              >
                <option value="all" className="bg-gray-900 text-white">All Status</option>
                <option value="planning" className="bg-gray-900 text-white">Planning</option>
                <option value="in-progress" className="bg-gray-900 text-white">In Progress</option>
                <option value="review" className="bg-gray-900 text-white">Review</option>
                <option value="completed" className="bg-gray-900 text-white">Completed</option>
                <option value="cancelled" className="bg-gray-900 text-white">Cancelled</option>
              </select>
            </div>

            {/* View Mode */}
            <div className="flex items-center gap-1 p-1 glass-mockup rounded-lg border border-white/10">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Grid View"
              >
                <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                </div>
              </button>
              <button
                onClick={() => setViewMode('timeline')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'timeline' 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Timeline View"
              >
                <CalendarIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`p-2 rounded transition-all ${
                  viewMode === 'kanban' 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Kanban View"
              >
                <ListBulletIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Projects Grid/List */}
        {filteredProjects.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-mockup rounded-xl border border-white/10 p-12 text-center"
          >
            <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No projects found</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery || selectedStatus !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first project from a template'
              }
            </p>
            {!searchQuery && selectedStatus === 'all' && (
              <button
                onClick={() => window.location.href = '/project-templates'}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Browse Templates
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={
              viewMode === 'grid' 
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                : "space-y-4"
            }
          >
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
                onStatusChange={handleUpdateProjectStatus}
                onView={() => {
                  setSelectedProject(project)
                  setShowProjectModal(true)
                }}
              />
            ))}
          </motion.div>
        )}

        {/* Project Detail Modal */}
        {selectedProject && showProjectModal && (
          <ProjectDetailModal
            project={selectedProject}
            onClose={() => {
              setSelectedProject(null)
              setShowProjectModal(false)
            }}
          />
        )}
      </main>
    </div>
  )
}

// Project Card Component
interface ProjectCardProps {
  project: ProjectInstance
  index: number
  onStatusChange: (projectId: string, status: ProjectStatus) => void
  onView: () => void
}

function ProjectCard({ project, index, onStatusChange, onView }: ProjectCardProps) {
  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case 'planning': return 'text-blue-400 bg-blue-500/20'
      case 'in-progress': return 'text-green-400 bg-green-500/20'
      case 'review': return 'text-yellow-400 bg-yellow-500/20'
      case 'completed': return 'text-purple-400 bg-purple-500/20'
      case 'cancelled': return 'text-red-400 bg-red-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const getStatusIcon = (status: ProjectStatus) => {
    switch (status) {
      case 'planning': return ClockIcon
      case 'in-progress': return PlayIcon
      case 'review': return PauseIcon
      case 'completed': return CheckCircleIcon
      case 'cancelled': return ExclamationTriangleIcon
      default: return ClockIcon
    }
  }

  const StatusIcon = getStatusIcon(project.status as ProjectStatus)
  
  // Calculate progress from tasks
  const totalTasks = project.tasks.length
  const completedTasks = project.tasks.filter(task => task.status === 'completed').length
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 + index * 0.05 }}
      className="glass-mockup rounded-xl border border-white/10 hover:border-white/20 transition-all p-6 hover:scale-105"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-white mb-1">{project.name}</h3>
          {project.template && (
            <p className="text-sm text-gray-400">From: {project.template.name}</p>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(project.status as ProjectStatus)}`}>
            <StatusIcon className="w-3 h-3" />
            {project.status}
          </span>
        </div>
      </div>

      {/* Progress */}
      {totalTasks > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-400">Progress</span>
            <span className="text-white">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
        {project.startDate && (
          <div className="flex items-center gap-1 text-gray-400">
            <CalendarIcon className="w-4 h-4" />
            <span>{new Date(project.startDate).toLocaleDateString()}</span>
          </div>
        )}
        {totalTasks > 0 && (
          <div className="flex items-center gap-1 text-gray-400">
            <ListBulletIcon className="w-4 h-4" />
            <span>{completedTasks}/{totalTasks}</span>
          </div>
        )}
        {project.template?.estimatedDuration && (
          <div className="flex items-center gap-1 text-gray-400">
            <ClockIcon className="w-4 h-4" />
            <span>{project.template.estimatedDuration}d</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-4 border-t border-white/10">
        <button
          onClick={onView}
          className="inline-flex items-center px-3 py-1 text-sm text-blue-400 hover:text-blue-300 transition-colors"
        >
          <EyeIcon className="w-4 h-4 mr-1" />
          View Details
        </button>
        
        <div className="flex items-center gap-1">
          {project.status === 'planning' && (
            <button
              onClick={() => onStatusChange(project.id, 'in-progress')}
              className="p-1 text-green-400 hover:text-green-300 transition-colors"
              title="Start Project"
            >
              <PlayIcon className="w-4 h-4" />
            </button>
          )}
          {project.status === 'in-progress' && (
            <button
              onClick={() => onStatusChange(project.id, 'review')}
              className="p-1 text-yellow-400 hover:text-yellow-300 transition-colors"
              title="Put on Review"
            >
              <PauseIcon className="w-4 h-4" />
            </button>
          )}
          {project.status === 'review' && (
            <button
              onClick={() => onStatusChange(project.id, 'completed')}
              className="p-1 text-purple-400 hover:text-purple-300 transition-colors"
              title="Mark as Completed"
            >
              <CheckCircleIcon className="w-4 h-4" />
            </button>
          )}
          <button
            className="p-1 text-gray-400 hover:text-white transition-colors"
            title="Edit Project"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  )
}

// Project Detail Modal Component
interface ProjectDetailModalProps {
  project: ProjectInstance
  onClose: () => void
}

function ProjectDetailModal({ project, onClose }: ProjectDetailModalProps) {
  const { showSuccess, showError } = useNotification()
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'timeline'>('overview')
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    name: project.name,
    description: project.description || '',
    startDate: project.startDate || '',
    shootDate: project.shootDate || '',
    agreedPrice: project.agreedPrice || 0
  })

  const handleUpdateTask = async (taskId: string, status: 'pending' | 'in-progress' | 'completed' | 'cancelled') => {
    try {
      const result = await projectTemplateService.updateTaskStatus(taskId, status)
      if (result.success) {
        showSuccess('Success', 'Task status updated')
        // Refresh project data
        window.location.reload() // Simple refresh for now
      } else {
        showError('Error', result.error || 'Failed to update task')
      }
    } catch (error) {
      console.error('Error updating task:', error)
      showError('Error', 'Failed to update task')
    }
  }

  const handleSaveProject = async () => {
    try {
      const result = await projectTemplateService.updateProject(project.id, editData)
      if (result.success) {
        showSuccess('Success', 'Project updated successfully')
        setIsEditing(false)
        window.location.reload() // Simple refresh for now
      } else {
        showError('Error', result.error || 'Failed to update project')
      }
    } catch (error) {
      console.error('Error updating project:', error)
      showError('Error', 'Failed to update project')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning':
      case 'pending': return 'text-blue-400 bg-blue-500/20'
      case 'in-progress': return 'text-green-400 bg-green-500/20'
      case 'review': return 'text-yellow-400 bg-yellow-500/20'
      case 'completed': return 'text-purple-400 bg-purple-500/20'
      case 'cancelled': return 'text-red-400 bg-red-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const totalTasks = project.tasks.length
  const completedTasks = project.tasks.filter(task => task.status === 'completed').length
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="glass-mockup rounded-2xl border border-white/10 max-w-5xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({...editData, name: e.target.value})}
                  className="text-xl font-semibold text-white bg-transparent border border-white/20 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              ) : (
                <h2 className="text-xl font-semibold text-white">{project.name}</h2>
              )}
              <div className="flex items-center gap-4 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(project.status)}`}>
                  <div className="w-2 h-2 bg-current rounded-full"></div>
                  {project.status}
                </span>
                {project.template && (
                  <span className="text-gray-400 text-sm">From: {project.template.name}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveProject}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  title="Edit Project"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
              )}
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-white transition-colors">
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-white/10">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: ChartBarIcon },
              { id: 'tasks', label: 'Tasks', icon: ClipboardDocumentListIcon },
              { id: 'timeline', label: 'Timeline', icon: CalendarIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Progress Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="glass-mockup rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Progress</span>
                    <span className="text-white font-bold">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="glass-mockup rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Tasks</span>
                    <span className="text-white font-bold">{completedTasks}/{totalTasks}</span>
                  </div>
                </div>
                
                <div className="glass-mockup rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Duration</span>
                    <span className="text-white font-bold">{project.template?.estimatedDuration || 0}d</span>
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="glass-mockup rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4">Project Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                    {isEditing ? (
                      <textarea
                        value={editData.description}
                        onChange={(e) => setEditData({...editData, description: e.target.value})}
                        rows={3}
                        className="w-full glass-mockup rounded-lg border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 p-3"
                        placeholder="Project description..."
                      />
                    ) : (
                      <p className="text-white">{project.description || 'No description provided'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Start Date</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editData.startDate}
                          onChange={(e) => setEditData({...editData, startDate: e.target.value})}
                          className="w-full glass-mockup rounded-lg border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 p-3"
                        />
                      ) : (
                        <p className="text-white">{project.startDate ? new Date(project.startDate).toLocaleDateString() : 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Shoot Date</label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={editData.shootDate}
                          onChange={(e) => setEditData({...editData, shootDate: e.target.value})}
                          className="w-full glass-mockup rounded-lg border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 p-3"
                        />
                      ) : (
                        <p className="text-white">{project.shootDate ? new Date(project.shootDate).toLocaleDateString() : 'Not set'}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Agreed Price</label>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editData.agreedPrice}
                          onChange={(e) => setEditData({...editData, agreedPrice: Number(e.target.value)})}
                          className="w-full glass-mockup rounded-lg border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 p-3"
                          placeholder="0"
                        />
                      ) : (
                        <p className="text-white">${project.agreedPrice || 0}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Task Management</h3>
              {totalTasks === 0 ? (
                <div className="text-center py-8">
                  <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-400">No tasks available for this project</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {project.tasks.map((task) => (
                    <div key={task.id} className="glass-mockup rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{task.title}</h4>
                          {task.dueDate && (
                            <p className="text-gray-400 text-sm">Due: {new Date(task.dueDate).toLocaleDateString()}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                          <div className="flex items-center gap-1">
                            {task.status === 'pending' && (
                              <button
                                onClick={() => handleUpdateTask(task.id, 'in-progress')}
                                className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                title="Start Task"
                              >
                                <PlayIcon className="w-4 h-4" />
                              </button>
                            )}
                            {task.status === 'in-progress' && (
                              <button
                                onClick={() => handleUpdateTask(task.id, 'completed')}
                                className="p-1 text-purple-400 hover:text-purple-300 transition-colors"
                                title="Complete Task"
                              >
                                <CheckCircleIcon className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">Project Timeline</h3>
              <div className="text-center py-8">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-400 mb-2">Timeline View</p>
                <p className="text-gray-500 text-sm">Calendar integration and timeline visualization will be implemented here</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
} 